// src/components/common/MergeFromSubmissionModal.tsx
/**
 * Applies details captured on a public sign-up to an existing member record, field by field.
 *
 * ⚠️ **Why this exists.** The import match-or-creates: a *new* member is built from the submission's
 * full details, but an *existing* match is only linked — the submitted gender, date of birth and
 * contacts never reach the member. So the intake/request page shows a date of birth and the
 * registration/case page shows a blank for the same person. Nothing was lost (the values stay on the
 * staging rows), it simply was never merged.
 *
 * ⚠️ **Every field is the admin's choice, and nothing is applied silently.** These values come from an
 * anonymous public form; the member record is maintained by the office. Fields the member leaves blank
 * are pre-ticked because filling a gap is safe. Fields that would **replace** an existing value start
 * unticked and are marked, because a differing contact is usually why the person failed to match on
 * contact in the first place — the maintained record should win unless someone decides otherwise.
 *
 * ⚠️ Writes with **`patch`, never `update`**. `CrudService.update` is a `PUT` that replaces the whole
 * entity, so sending four fields would blank everything else on the member.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Group, Modal, Stack, Text,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useMemberService, type Member } from '@actschurch/ccms-sdk';
import { formatBirthDate } from './MemberDetailModal';

/** The member fields a public submission can contribute to. */
type MergeableKey = 'dateOfBirth' | 'gender' | 'email' | 'cellNumber';

interface MergeRow {
  key: MergeableKey;
  label: string;
  /** What the member record holds now. */
  currentValue: string | null;
  /** What the sign-up captured. */
  submittedValue: string;
  /** True when the member has nothing here — the safe case. */
  isFill: boolean;
}

export interface MergeFromSubmissionModalProps {
  memberID: number;
  memberName: string;
  /** The member's current values. */
  current: {
    dateOfBirth?: string | null;
    gender?: string | null;
    email?: string | null;
    cellNumber?: string | null;
  };
  /** What the person gave on the intake/request. */
  submitted: {
    dateOfBirth?: string | null;
    gender?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  /** "sign-up" / "request" — wording only. */
  sourceLabel: string;
  opened: boolean;
  onClose: () => void;
  onMerged: () => void | Promise<void>;
}

const norm = (v?: string | null) => (v ?? '').trim();

export default function MergeFromSubmissionModal({
  memberID, memberName, current, submitted, sourceLabel, opened, onClose, onMerged,
}: MergeFromSubmissionModalProps) {
  const memberService = useMemberService();
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  /** Only fields the submission actually carries and that would change something. */
  const rows = useMemo<MergeRow[]>(() => {
    const candidates: { key: MergeableKey; label: string; cur?: string | null; sub?: string | null }[] = [
      { key: 'dateOfBirth', label: 'Date of birth', cur: current.dateOfBirth, sub: submitted.dateOfBirth },
      { key: 'gender', label: 'Gender', cur: current.gender, sub: submitted.gender },
      { key: 'email', label: 'Email', cur: current.email, sub: submitted.email },
      { key: 'cellNumber', label: 'Phone', cur: current.cellNumber, sub: submitted.phone },
    ];

    return candidates.flatMap(({ key, label, cur, sub }) => {
      const submittedValue = norm(sub);
      const currentValue = norm(cur);
      // Nothing to offer, or the record already agrees.
      if (!submittedValue || submittedValue === currentValue) return [];
      return [{
        key,
        label,
        currentValue: currentValue || null,
        submittedValue,
        isFill: currentValue === '',
      }];
    });
  }, [current, submitted]);

  // Pre-tick the safe fills only. Replacements are a deliberate act.
  useEffect(() => {
    if (!opened) return;
    setSelected(Object.fromEntries(rows.filter((r) => r.isFill).map((r) => [r.key, true])));
  }, [opened, rows]);

  const chosen = rows.filter((r) => selected[r.key]);
  const replacing = chosen.filter((r) => !r.isFill);

  const handleApply = async () => {
    if (chosen.length === 0 || busy) return;
    setBusy(true);
    try {
      const payload: Partial<Member> = {};
      chosen.forEach((r) => { (payload as Record<string, string>)[r.key] = r.submittedValue; });

      // ⚠️ `patch`, not `update` — see the note above. A PUT here would blank the rest of the member.
      await memberService.patch(memberID, payload);

      await onMerged();
      toast.success(
        'Member updated',
        `${chosen.length} field${chosen.length === 1 ? '' : 's'} applied to ${memberName}.`,
      );
      onClose();
    } catch (e) {
      console.error('[MergeFromSubmission] patch failed:', e);
      toast.error('Could not update the member', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const display = (r: MergeRow, value: string) =>
    (r.key === 'dateOfBirth' ? (formatBirthDate(value) ?? value) : value);

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title={`Fill ${memberName}'s details from the ${sourceLabel}`}
      size="lg"
      centered
    >
      <Stack gap="md">
        {rows.length === 0 ? (
          <Alert color="teal" variant="light" icon={<IconInfoCircle size={16} />}>
            <Text size="sm">
              Nothing to apply — the member record already matches everything the {sourceLabel}
              {' '}captured.
            </Text>
          </Alert>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              These details were given on the {sourceLabel} but are not on {memberName}&apos;s member
              record. Choose which to apply.
            </Text>

            <Stack gap="xs">
              {rows.map((r) => (
                <Card key={r.key} withBorder radius="md" padding="sm">
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Checkbox
                        checked={Boolean(selected[r.key])}
                        disabled={busy}
                        onChange={(e) => setSelected((prev) => ({
                          ...prev, [r.key]: e.currentTarget.checked,
                        }))}
                      />
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6}>
                          <Text size="sm" fw={600}>{r.label}</Text>
                          {r.isFill
                            ? <Badge size="xs" variant="light" color="teal">Currently blank</Badge>
                            : <Badge size="xs" variant="light" color="orange">Would replace</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {/* Both values shown, so a replacement is never made blind. */}
                          {r.isFill
                            ? `Set to “${display(r, r.submittedValue)}”`
                            : `“${display(r, r.currentValue ?? '')}” → “${display(r, r.submittedValue)}”`}
                        </Text>
                      </div>
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>

            {replacing.length > 0 && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  {replacing.length === 1 ? 'One field replaces' : `${replacing.length} fields replace`}
                  {' '}a value already on the member record. The {sourceLabel} was filled in by a member
                  of the public; the member record is maintained by the office.
                </Text>
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={handleApply}
            loading={busy}
            disabled={chosen.length === 0 || busy}
            color={replacing.length > 0 ? 'orange' : undefined}
          >
            {chosen.length > 0 ? `Apply ${chosen.length}` : 'Apply'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
